import React, { useEffect, useMemo, useState } from "react";

import Modal from "../masters/Modal";

import {
  allStatutoryStateOptions,
  CENTRAL_STATE_LABEL,
  displayActLabel,
  stateLabelToCode,
} from "./statutoryStateUtils";

export default function StatutoryAliasModal({
  isOpen,
  onClose,
  catalog,
  branchStates = [],
  aliases = [],
  onSave,
  onDelete,
}) {
  const stateOptions = useMemo(
    () => allStatutoryStateOptions(branchStates),
    [branchStates]
  );

  const allForms = catalog.allForms || catalog.forms || [];
  const allActs = catalog.allActs || catalog.acts || [];

  const [aliasState, setAliasState] = useState("");
  const [aliasActCode, setAliasActCode] = useState("");
  const [aliasFormCode, setAliasFormCode] = useState("");
  const [copyFromFormCode, setCopyFromFormCode] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [description, setDescription] = useState("");
  const [editingId, setEditingId] = useState(null);

  const stateCode = useMemo(() => stateLabelToCode(aliasState), [aliasState]);

  // Target state can be any branch/Indian state; Act lists catalog templates
  // (state acts + Central) so forms can be copied across states.
  const actsForState = useMemo(() => {
    if (!aliasState) return [];
    return [...allActs];
  }, [allActs, aliasState]);

  const formsForAct = useMemo(() => {
    if (!aliasActCode) return [];
    return allForms.filter(
      (f) => f.actCode === aliasActCode || (f.actCodes || []).includes(aliasActCode)
    );
  }, [allForms, aliasActCode]);

  const copySourceOptions = useMemo(
    () =>
      allForms.map((f) => {
        const act = allActs.find((a) => a.actCode === f.actCode);
        const actName = displayActLabel(act?.label || f.actCode);
        return {
          formCode: f.formCode,
          label: `${f.defaultLabel || f.formCode} (${actName})`,
          actCode: f.actCode,
          defaultLabel: f.defaultLabel,
        };
      }),
    [allForms, allActs]
  );

  useEffect(() => {
    if (!isOpen) return;
    setAliasState("");
    setAliasActCode("");
    setAliasFormCode("");
    setCopyFromFormCode("");
    setCustomLabel("");
    setDescription("");
    setEditingId(null);
  }, [isOpen]);

  const applyCopyFrom = (formCode) => {
    setCopyFromFormCode(formCode);
    if (!formCode) return;
    const source = allForms.find((f) => f.formCode === formCode);
    if (!source) return;
    const act = allActs.find((a) => a.actCode === source.actCode);
    // Prefill destination state only when empty — do not overwrite a chosen target state
    if (!aliasState) {
      if (act?.stateCode === "IN") setAliasState(CENTRAL_STATE_LABEL);
      else if (act?.stateCode === "KA") setAliasState("Karnataka");
    }
    setAliasActCode(source.actCode);
    setAliasFormCode(source.formCode);
    setCustomLabel(source.defaultLabel || source.formCode);
    setDescription(source.legalReference || "");
  };

  const loadExistingAlias = (alias) => {
    setEditingId(alias.id);
    setAliasState(
      alias.stateLabel ||
        (alias.stateCode === "IN" ? CENTRAL_STATE_LABEL : alias.stateCode)
    );
    setAliasActCode(alias.actCode);
    setAliasFormCode(alias.formCode);
    setCustomLabel(alias.customLabel || "");
    setDescription(alias.description || "");
    setCopyFromFormCode(alias.copyFromFormCode || alias.formCode);
  };

  const inputClass =
    "h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 text-xs";

  const handleSave = () => {
    if (!aliasState || !aliasActCode || !aliasFormCode || !customLabel.trim()) return;
    onSave({
      id: editingId || undefined,
      stateCode,
      stateLabel: aliasState,
      actCode: aliasActCode,
      formCode: aliasFormCode,
      customLabel: customLabel.trim(),
      description: description.trim() || null,
      copyFromFormCode: copyFromFormCode || aliasFormCode,
    });
    setCustomLabel("");
    setDescription("");
    setCopyFromFormCode("");
    setEditingId(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCancel={onClose}
      onSubmit={handleSave}
      submitText={editingId ? "Update form name" : "Save form name"}
      title="Manage form names by state & act"
      maxWidthClass="max-w-3xl"
    >
      <p className="ds-caption mb-3 text-[hsl(var(--ds-muted-fg))]">
        Choose a source form to copy, then set the display name and rule reference (e.g. See Rule 19(2)) for your branch state.
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="ds-caption mb-1 block">Copy from form (template source)</label>
          <select
            className={inputClass}
            value={copyFromFormCode}
            onChange={(e) => applyCopyFrom(e.target.value)}
          >
            <option value="">-- Select form to copy --</option>
            {copySourceOptions.map((f) => (
              <option key={f.formCode} value={f.formCode}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="ds-caption mb-1 block">State</label>
          <select
            className={inputClass}
            value={aliasState}
            onChange={(e) => {
              setAliasState(e.target.value);
              setAliasActCode("");
              setAliasFormCode("");
            }}
          >
            <option value="">-- Select State --</option>
            {stateOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="ds-caption mb-1 block">Act</label>
          <select
            className={inputClass}
            value={aliasActCode}
            onChange={(e) => {
              setAliasActCode(e.target.value);
              setAliasFormCode("");
            }}
            disabled={!aliasState}
          >
            <option value="">-- Select Act --</option>
            {actsForState.map((a) => (
              <option key={a.actCode} value={a.actCode}>
                {displayActLabel(a.label)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="ds-caption mb-1 block">Form</label>
          <select
            className={inputClass}
            value={aliasFormCode}
            onChange={(e) => setAliasFormCode(e.target.value)}
            disabled={!aliasActCode}
          >
            <option value="">-- Select Form --</option>
            {formsForAct.map((f) => (
              <option key={f.formCode} value={f.formCode}>
                {f.defaultLabel || f.formCode}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="ds-caption mb-1 block">Custom form name</label>
          <input
            className={inputClass}
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            placeholder="Display name on reports"
          />
        </div>
        <div className="md:col-span-2">
          <label className="ds-caption mb-1 block">Description / rule reference</label>
          <textarea
            className={`${inputClass} min-h-[72px] py-2`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. See Rule 19(2) of Karnataka Shops & Establishment Rules"
          />
        </div>
      </div>
      {aliases.length > 0 && (
        <div className="mt-4 rounded-md border border-[hsl(var(--ds-border))] p-3">
          <p className="ds-caption mb-2 font-semibold">Saved names — click to edit</p>
          <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
            {aliases.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  className="text-left hover:underline"
                  onClick={() => loadExistingAlias(a)}
                >
                  {a.customLabel} — {a.formCode} ({a.stateCode})
                  {a.description ? ` · ${a.description.slice(0, 60)}` : ""}
                </button>
                {onDelete && (
                  <button
                    type="button"
                    className="shrink-0 text-red-600 hover:underline"
                    onClick={() => onDelete(a.id)}
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}
